"use client";

import React from "react";
import { ImageResponseData } from "@/restapi/image";
import { IoMdLock } from "react-icons/io";
import "./style.css";

export type ImageGridCardDeleteOptions = {
  isDeleteMode: boolean;
  selectedToDelete: string | null;
  handleDeleteImageCheck: (imageId: string) => void;
};

type ImageGridCardProps = {
  image: ImageResponseData;
  onClick?: (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    imageId: string
  ) => void;
  deleteOptions?: ImageGridCardDeleteOptions;
  bulkMode?: boolean;
  checked?: boolean;
  onToggleCheck?: (imageId: number) => void;
};

const ImageGridCard: React.FC<ImageGridCardProps> = ({
  image,
  onClick,
  deleteOptions,
  bulkMode = false,
  checked = false,
  onToggleCheck,
}) => {
  const fileUrl = image.imageFile.fileUrl;
  const fileName = image.imageFile.fileName;
  const isVideo = fileName?.toLowerCase().endsWith(".mp4");

  const handleToggleCheck = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleCheck?.(image.id);
  };

  const showCheckbox =
    deleteOptions?.isDeleteMode &&
    (!deleteOptions?.selectedToDelete ||
      deleteOptions?.selectedToDelete === image.uuid);

  return (
    <div
      className={[
        "relative group overflow-hidden rankScoreList rounded-lg",
        image.isLocked ? "bg-orange-500/5" : "",
      ].join(" ")}
    >
      <div
        id={`image-grid-card-${image.id}`}
        data-testid={`image-card-${image.uuid}`}
        className="cursor-pointer"
        onClick={(e) => {
          if (bulkMode) {
            e.preventDefault();
            e.stopPropagation();
            onToggleCheck?.(image.id);
            return;
          }
          onClick?.(e, image.uuid);
        }}
      >
        {isVideo ? (
          <video
            width={300}
            height={300}
            className="object-cover w-full aspect-square rounded-lg"
            src={fileUrl}
            muted
            loop
            playsInline
            autoPlay
          />
        ) : (
          <div className="relative w-full aspect-square overflow-hidden rounded-lg">
            <img
              src={fileUrl}
              alt={fileName ?? "image"}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
        )}

        {typeof image.averageScore === "number" &&
          !isNaN(image.averageScore) && (
            <div className="text-with-outline transform rankScoreTxtBox">
              <span className="rankScoreTxt">{image.averageScore}</span>
            </div>
          )}
      </div>

      {image.isLocked && (
        <div className="absolute top-2 left-2 z-10 max-w-[85%]">
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap bg-orange-500/15 text-orange-200 border border-orange-400/20 backdrop-blur-sm"
            title={image.lockReason ?? "Locked"}
          >
            <IoMdLock className="w-3 h-3" />
            Locked
          </span>

          {image.lockReason && (
            <div
              className="mt-1 rounded-md bg-black/60 px-2 py-1 text-[10px] text-orange-100/90 leading-tight line-clamp-2"
              title={image.lockReason}
            >
              {image.lockReason}
            </div>
          )}
        </div>
      )}

      {bulkMode && (
        <button
          type="button"
          aria-label="select image"
          onClick={handleToggleCheck}
          className={[
            "absolute top-2 right-2 z-20 mt-1 w-5 h-5 rounded border",
            checked
              ? "bg-white/80 border-white/60"
              : "bg-transparent border-white/25",
            "flex items-center justify-center",
            "hover:bg-white/10",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30",
          ].join(" ")}
        >
          {checked && (
            <span className="text-black text-[12px] leading-none">✓</span>
          )}
        </button>
      )}

      {!bulkMode && showCheckbox && (
        <div className="absolute top-2 right-2 z-10">
          <button
            type="button"
            aria-label="select image for delete"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              deleteOptions?.handleDeleteImageCheck(image.uuid);
            }}
            className="mt-1 w-5 h-5 rounded border bg-white/80 border-white/60 flex items-center justify-center hover:bg-white/90"
          >
            <span className="text-black text-[12px] leading-none">✓</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ImageGridCard;